import { BadgeCheck, CircleDashed, Sigma } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Quality, Status } from '../lib/status';
import { qualityLabel, qualityMeaning, statusIcon, statusLabel } from '../lib/status';

interface StatusChipProps {
  status: Status;
  /** Overrides the status's own word, when the section has something more specific to say. */
  label?: ReactNode;
  /** Keeps the icon and drops the word, for a tight row. The word still reaches a screen reader. */
  compact?: boolean;
  title?: string;
}

/**
 * A status, drawn the same way everywhere.
 *
 * <p>Three encodings, always together: the shape of the icon, the colour, and the word. Take the
 * colour away - greyscale, a cheap projector, a reader who does not distinguish red from green -
 * and the chip still says what it means. That is the whole reason this component exists rather
 * than a coloured dot per screen.
 */
export default function StatusChip({ status, label, compact = false, title }: StatusChipProps) {
  const Icon = statusIcon(status);
  const word = label ?? statusLabel(status);

  return (
    <span className={`chip chip--${status}`} title={title}>
      <Icon size={14} aria-hidden="true" />
      {compact ? <span className="sr-only">{statusLabel(status)}</span> : <span>{word}</span>}
    </span>
  );
}

const QUALITY_ICONS = {
  verified: BadgeCheck,
  estimated: Sigma,
  unavailable: CircleDashed,
} as const;

/**
 * How far a number can be trusted, which the report says out loud rather than leaving to a
 * footnote: verified against this door, estimated from the area around it, or not available at
 * all. It is deliberately a different shape from {@link StatusChip} - a quiet outline rather than
 * a filled chip - because it answers a different question from "is this good".
 */
export function QualityChip({ quality, title }: { quality: Quality; title?: string }) {
  const Icon = QUALITY_ICONS[quality];

  return (
    <span className={`quality quality--${quality}`} title={title ?? qualityMeaning(quality)}>
      <Icon size={13} aria-hidden="true" />
      <span>{qualityLabel(quality)}</span>
    </span>
  );
}
